import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mic, Square, Play, Pause, RotateCcw, Upload, Sparkles,
  Library, Search, X, Loader2, CheckCircle2,
} from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * VoiceSampleControls
 *
 * Renders the "Voice Sample" area of the Actor dialog with three modes:
 *   - upload a file
 *   - record a fresh sample (browser MediaRecorder)
 *   - preview / re-record
 *
 * Exposes state up to the parent via `onSampleChange(file, previewUrl)`.
 * Also exposes a "Clone Voice" button that uploads the current sample,
 * calls /api/ai/clone-voice, and passes the resulting voice_id back
 * via `onCloned(voiceId)`.
 */
export function VoiceSampleControls({ initialSampleUrl, onSampleChange, onCloned, cloneCredits = 20 }) {
  const [mode, setMode] = useState("idle"); // idle | recording | preview
  const [sampleFile, setSampleFile] = useState(null); // File or Blob
  const [previewUrl, setPreviewUrl] = useState(initialSampleUrl || null);
  const [recElapsed, setRecElapsed] = useState(0);
  const [cloning, setCloning] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const emitChange = (file, url) => {
    setSampleFile(file);
    setPreviewUrl(url);
    if (onSampleChange) onSampleChange(file, url);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      let mime = "";
      for (const m of mimeCandidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("mp4") ? "m4a" : type.includes("webm") ? "webm" : "ogg";
        const filename = `recording_${Date.now()}.${ext}`;
        // Wrap Blob as File so downstream form-data submission carries a filename.
        const asFile = new File([blob], filename, { type });
        const url = URL.createObjectURL(blob);
        emitChange(asFile, url);
        setMode("preview");
        // Release mic
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setMode("recording");
      setRecElapsed(0);
      timerRef.current = setInterval(() => setRecElapsed((s) => s + 1), 1000);
    } catch (err) {
      toast.error("Microphone permission denied or unavailable");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const resetSample = () => {
    if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    emitChange(null, null);
    setMode("idle");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFileSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Voice sample must be under 20 MB");
      return;
    }
    const url = URL.createObjectURL(f);
    emitChange(f, url);
    setMode("preview");
  };

  const cloneNow = async () => {
    if (!sampleFile) {
      toast.error("Record or upload a voice sample first");
      return;
    }
    setCloning(true);
    try {
      // Step 1 — upload the sample to get a signed URL
      const fd = new FormData();
      fd.append("file", sampleFile);
      const upResp = await axios.post(`${BACKEND_URL}/api/ai/upload-voice-sample`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const audioUrl = upResp.data?.url;
      if (!audioUrl) throw new Error("Voice sample upload failed");

      // Step 2 — trigger clone
      const cloneResp = await axios.post(
        `${BACKEND_URL}/api/ai/clone-voice`,
        { audio_url: audioUrl, voice_name: `Actor voice ${Date.now()}` },
        { withCredentials: true }
      );
      const result = cloneResp.data?.result || {};
      const voiceId = result.voice_id || result.id || cloneResp.data?.voice_id;
      if (!voiceId) throw new Error("Cloning returned no voice ID");
      toast.success(`Voice cloned! ID: ${voiceId.slice(0, 12)}…`);
      if (onCloned) onCloned(voiceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Voice cloning failed");
    } finally {
      setCloning(false);
    }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="space-y-2" data-testid="voice-controls-root">
      <label className="text-sm font-medium text-gray-700">Voice Sample (for cloning)</label>

      {mode === "idle" && !previewUrl && (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 text-center transition-colors group"
            data-testid="voice-upload-btn"
          >
            <Upload className="w-6 h-6 mx-auto text-gray-400 group-hover:text-blue-500 mb-1" />
            <p className="text-sm text-gray-600 font-medium">Upload</p>
            <p className="text-[11px] text-gray-400">MP3 / WAV / OGG · max 20 MB</p>
          </button>
          <button
            type="button"
            onClick={startRecording}
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-red-400 text-center transition-colors group"
            data-testid="voice-record-btn"
          >
            <Mic className="w-6 h-6 mx-auto text-gray-400 group-hover:text-red-500 mb-1" />
            <p className="text-sm text-gray-600 font-medium">Record</p>
            <p className="text-[11px] text-gray-400">Use your microphone</p>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={onFileSelected}
            data-testid="voice-file-input"
          />
        </div>
      )}

      {mode === "recording" && (
        <div className="border-2 border-red-300 bg-red-50 rounded-lg p-4 flex items-center justify-between" data-testid="voice-recording-panel">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm font-medium text-red-700">Recording…</span>
            <span className="text-sm font-mono text-red-600">{fmt(recElapsed)}</span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={stopRecording}
            data-testid="voice-stop-btn"
          >
            <Square className="w-3 h-3 mr-1.5" /> Stop
          </Button>
        </div>
      )}

      {mode === "preview" && previewUrl && (
        <div className="border border-gray-200 bg-gray-50 rounded-lg p-3 space-y-2" data-testid="voice-preview-panel">
          <audio controls src={previewUrl} className="w-full h-10" data-testid="voice-preview-audio" />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={resetSample}
              data-testid="voice-reset-btn"
            >
              <RotateCcw className="w-3 h-3 mr-1.5" /> Re-record / re-upload
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={cloneNow}
              disabled={cloning}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white"
              data-testid="voice-clone-btn"
            >
              {cloning ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Cloning…</>
              ) : (
                <><Sparkles className="w-3 h-3 mr-1.5" /> Clone Voice ({cloneCredits} credits)</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * VoiceLibraryModal
 *
 * Full-screen modal to browse & filter ElevenLabs voices from
 * `/api/ai/voices`. Clicking "Use this voice" invokes onPick(voiceId)
 * and closes the modal.
 */
export function VoiceLibraryModal({ open, onClose, onPick }) {
  const [loading, setLoading] = useState(false);
  const [voices, setVoices] = useState([]);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [accent, setAccent] = useState("");
  const [language, setLanguage] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const fetchVoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (gender) params.gender = gender;
      if (age) params.age = age;
      if (accent) params.accent = accent;
      if (language) params.language = language;
      const r = await axios.get(`${BACKEND_URL}/api/ai/voices`, {
        params, withCredentials: true,
      });
      const data = r.data;
      const list = Array.isArray(data) ? data : (data.voices || data.items || []);
      setVoices(list);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load voices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchVoices();
  }, [open]);

  // Debounced re-fetch when filters change
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchVoices, 350);
    return () => clearTimeout(t);
  }, [search, gender, age, accent, language]);

  const togglePreview = (v) => {
    const url = v.preview_url || v.preview || v.sample_url;
    if (!url) { toast.info("No preview available"); return; }
    if (playingId === v.voice_id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = url;
      audioRef.current.play().catch(() => toast.error("Preview playback failed"));
      setPlayingId(v.voice_id);
    }
  };

  const filterOptions = useMemo(() => ({
    gender: ["", "male", "female", "neutral"],
    age: ["", "young", "middle_aged", "old"],
    accent: ["", "american", "british", "indian", "australian", "irish"],
    language: ["", "en", "hi", "es", "fr", "de", "pt", "ar", "zh"],
  }), []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="voice-library-modal">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Library className="w-5 h-5 text-indigo-500" />
            <h3 className="text-lg font-semibold">ElevenLabs Voice Library</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="voice-library-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b bg-gray-50 grid grid-cols-1 md:grid-cols-5 gap-2">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or description…"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="voice-library-search"
            />
          </div>
          {[
            { label: "Gender", state: gender, set: setGender, opts: filterOptions.gender },
            { label: "Age", state: age, set: setAge, opts: filterOptions.age },
            { label: "Accent", state: accent, set: setAccent, opts: filterOptions.accent },
            { label: "Language", state: language, set: setLanguage, opts: filterOptions.language },
          ].map((cfg) => (
            <select
              key={cfg.label}
              className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              value={cfg.state}
              onChange={(e) => cfg.set(e.target.value)}
              data-testid={`voice-library-filter-${cfg.label.toLowerCase()}`}
            >
              {cfg.opts.map((o) => (
                <option key={o} value={o}>{o ? o.replace("_", " ") : `Any ${cfg.label}`}</option>
              ))}
            </select>
          ))}
        </div>

        {/* Voice grid */}
        <div className="flex-1 overflow-y-auto p-5" data-testid="voice-library-grid">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : voices.length === 0 ? (
            <div className="text-center text-gray-500 py-10">No voices found. Try clearing filters.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {voices.map((v) => {
                const vid = v.voice_id || v.id;
                const labels = v.labels || {};
                return (
                  <div
                    key={vid}
                    className="border border-gray-200 rounded-lg p-3 hover:border-indigo-400 hover:shadow-sm transition-all bg-white"
                    data-testid={`voice-card-${vid}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{v.name || "Unnamed"}</p>
                        <p className="text-[11px] text-gray-500 line-clamp-2">
                          {v.description || labels.description || v.category || "—"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => togglePreview(v)}
                        className="w-8 h-8 flex-shrink-0 rounded-full bg-indigo-100 hover:bg-indigo-200 flex items-center justify-center text-indigo-600"
                        title="Play preview"
                        data-testid={`voice-preview-${vid}`}
                      >
                        {playingId === vid ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {[labels.gender || v.gender, labels.age || v.age, labels.accent || v.accent, labels.language || v.language]
                        .filter(Boolean)
                        .slice(0, 4)
                        .map((tag, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {String(tag).replace("_", " ")}
                          </span>
                        ))}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="w-full h-7 text-xs"
                      onClick={() => { onPick(vid); onClose(); }}
                      data-testid={`voice-pick-${vid}`}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1.5" /> Use this voice
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <audio ref={audioRef} onEnded={() => setPlayingId(null)} className="hidden" />
      </div>
    </div>
  );
}
